import { useState } from "react";
import axios from "axios";
import { differenceInCalendarDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ChevronLeft,
  ChevronRight,
  Users,
  CalendarDays,
  Snowflake,
  ChefHat,
  Tv,
  Flame,
  Bath,
  Waves,
  Wifi,
  Accessibility,
  Droplets,
  Check,
  Loader2,
} from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { DECORACOES, TAXA_PET, formatBRL, CONTATO } from "../data/cabanas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  "Ar condicionado": Snowflake,
  "Mini cozinha": ChefHat,
  "Tv": Tv,
  "Lareira": Flame,
  "Banheiro privativo": Bath,
  "Piscina privativa": Waves,
  "Piscina": Waves,
  "Acesso à Piscina Coletiva": Waves,
  "Wifi": Wifi,
  "Acessibilidade": Accessibility,
  "Jacuzzi": Droplets,
};

const emptyForm = {
  nomeCompleto: "",
  dataNascimento: "",
  cpf: "",
  email: "",
  telefone: "",
  acompanhante: "",
  hospedes: "1",
  solicitacao: "",
};

export const CabinCard = ({ cabin }) => {
  const [idx, setIdx] = useState(0);
  const [range, setRange] = useState(undefined);
  const [form, setForm] = useState(emptyForm);
  const [pet, setPet] = useState("nao");
  const [decor, setDecor] = useState("nenhuma");
  const [pagamento, setPagamento] = useState("pix");
  const [parcelas, setParcelas] = useState("1");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const nights =
    range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0;
  const petFee = pet === "sim" ? TAXA_PET : 0;
  const decorOpt = DECORACOES.find((d) => d.id === decor) || DECORACOES[0];
  const total = nights * cabin.preco + petFee + decorOpt.preco;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const prev = () => setIdx((idx - 1 + cabin.fotos.length) % cabin.fotos.length);
  const next = () => setIdx((idx + 1) % cabin.fotos.length);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!nights) {
      setError("Selecione as datas de check-in e check-out no calendário acima.");
      return;
    }
    setSending(true);
    const payload = {
      cabana: cabin.nome,
      checkin: format(range.from, "dd/MM/yyyy"),
      checkout: format(range.to, "dd/MM/yyyy"),
      noites: nights,
      diaria: cabin.preco,
      ...form,
      hospedes: parseInt(form.hospedes, 10) || 1,
      pet,
      formaPagamento: pagamento === "pix" ? "Pix" : "Cartão de Crédito",
      parcelas: pagamento === "credito" ? parcelas : "",
      decoracao: decorOpt.nome,
      decoracaoPreco: decorOpt.preco,
      taxaPet: petFee,
      total,
    };
    try {
      await axios.post(`${API}/reservas`, payload);
      setSuccess(true);
    } catch (err) {
      setError("Não foi possível enviar sua reserva. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <article
      id={cabin.id}
      data-testid={`cabin-card-${cabin.id}`}
      className="bg-white rounded-2xl shadow-lg overflow-hidden border border-[#e2d9cd] flex flex-col"
    >
      <div className="relative aspect-[4/3] bg-[#1b4332]/10 group">
        <img
          src={cabin.fotos[idx]}
          alt={`${cabin.nome} - foto ${idx + 1}`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <button
          onClick={prev}
          data-testid={`cabin-carousel-prev-button-${cabin.id}`}
          aria-label="Foto anterior"
          className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#1b4332]/70 hover:bg-[#1b4332] text-white rounded-full p-2 transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={next}
          data-testid={`cabin-carousel-next-button-${cabin.id}`}
          aria-label="Próxima foto"
          className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#1b4332]/70 hover:bg-[#1b4332] text-white rounded-full p-2 transition-colors"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
        <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5">
          {cabin.fotos.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Foto ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === idx ? "w-5 bg-[#d4a373]" : "w-1.5 bg-white/60"
              }`}
            />
          ))}
        </div>
        <div className="absolute top-3 left-3 bg-[#1b4332]/85 text-[#f5f0eb] text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" /> Até {cabin.capacidade} hóspedes
        </div>
      </div>

      <div className="p-6 sm:p-8 flex flex-col gap-6 flex-1">
        <div>
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1b4332]">{cabin.nome}</h3>
            <p className="text-lg font-bold text-[#b85d39]" data-testid={`cabin-price-${cabin.id}`}>
              {formatBRL(cabin.preco)} <span className="text-xs text-[#526559] font-normal">/ diária</span>
            </p>
          </div>
          <p className="mt-3 text-sm text-[#526559] leading-relaxed">{cabin.descricao}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {[...cabin.comodidades, ...(cabin.comodidadesExtras || [])].map((c) => {
            const Icon = ICONS[c] || Check;
            return (
              <span
                key={c}
                className="inline-flex items-center gap-1.5 bg-[#f5f0eb] text-[#2d5a27] text-xs font-bold px-3 py-1.5 rounded-full border border-[#e2d9cd]"
              >
                <Icon className="h-3.5 w-3.5" /> {c}
              </span>
            );
          })}
        </div>

        <div className="border-t border-[#e2d9cd] pt-6">
          <p className="text-xs uppercase tracking-wider text-[#526559] font-bold mb-3 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-[#b85d39]" /> Escolha as datas da sua estadia
          </p>
          <div
            className="flex justify-center bg-[#faf7f2] rounded-xl border border-[#e2d9cd]"
            data-testid={`cabin-calendar-range-picker-${cabin.id}`}
          >
            <Calendar
              mode="range"
              selected={range}
              onSelect={setRange}
              locale={ptBR}
              disabled={{ before: new Date() }}
              numberOfMonths={1}
            />
          </div>
          {range?.from && (
            <p className="mt-2 text-xs text-center text-[#526559]" data-testid={`date-range-summary-${cabin.id}`}>
              {range.to
                ? `Check-in ${format(range.from, "dd/MM/yyyy")} · Check-out ${format(
                    range.to,
                    "dd/MM/yyyy"
                  )} · ${nights} ${nights === 1 ? "diária" : "diárias"}`
                : "Agora selecione a data de check-out"}
            </p>
          )}
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4" data-testid={`booking-form-${cabin.id}`}>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-[#1e2923]">Nome Completo *</span>
              <input
                required
                value={form.nomeCompleto}
                onChange={set("nomeCompleto")}
                data-testid={`booking-form-name-input-${cabin.id}`}
                className="border border-[#e2d9cd] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/40 bg-white"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-[#1e2923]">Data de Nascimento *</span>
              <input
                required
                type="date"
                value={form.dataNascimento}
                onChange={set("dataNascimento")}
                data-testid={`booking-form-birthdate-input-${cabin.id}`}
                className="border border-[#e2d9cd] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/40 bg-white"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-[#1e2923]">CPF *</span>
              <input
                required
                placeholder="000.000.000-00"
                value={form.cpf}
                onChange={set("cpf")}
                data-testid={`booking-form-cpf-input-${cabin.id}`}
                className="border border-[#e2d9cd] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/40 bg-white"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-[#1e2923]">E-mail *</span>
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                data-testid={`booking-form-email-input-${cabin.id}`}
                className="border border-[#e2d9cd] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/40 bg-white"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-[#1e2923]">Telefone (WhatsApp) *</span>
              <input
                required
                type="tel"
                placeholder="(51) 99999-9999"
                value={form.telefone}
                onChange={set("telefone")}
                data-testid={`booking-form-phone-input-${cabin.id}`}
                className="border border-[#e2d9cd] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/40 bg-white"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-[#1e2923]">Nome do Acompanhante</span>
              <input
                value={form.acompanhante}
                onChange={set("acompanhante")}
                data-testid={`booking-form-companion-input-${cabin.id}`}
                className="border border-[#e2d9cd] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/40 bg-white"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-[#1e2923]">Número de Hóspedes *</span>
              <select
                value={form.hospedes}
                onChange={set("hospedes")}
                data-testid={`booking-form-guests-select-${cabin.id}`}
                className="border border-[#e2d9cd] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/40 bg-white"
              >
                {Array.from({ length: cabin.capacidade }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1} {i === 0 ? "hóspede" : "hóspedes"}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-[#1e2923]">Leva pet? (+ {formatBRL(TAXA_PET)})</span>
              <div className="flex gap-3" data-testid={`booking-form-pet-radio-${cabin.id}`}>
                {["nao", "sim"].map((v) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => setPet(v)}
                    data-testid={`booking-form-pet-${v}-${cabin.id}`}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-bold border transition-colors ${
                      pet === v
                        ? "bg-[#2d5a27] text-white border-[#2d5a27]"
                        : "bg-white text-[#526559] border-[#e2d9cd] hover:border-[#2d5a27]"
                    }`}
                  >
                    {v === "sim" ? "Sim" : "Não"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-bold text-[#1e2923]">Decoração Romântica</span>
            <select
              value={decor}
              onChange={(e) => setDecor(e.target.value)}
              data-testid={`booking-form-decor-select-${cabin.id}`}
              className="border border-[#e2d9cd] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/40 bg-white"
            >
              {DECORACOES.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nome} {d.preco > 0 ? `(+ ${formatBRL(d.preco)})` : ""}
                </option>
              ))}
            </select>
          </label>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-bold text-[#1e2923]">Forma de Pagamento</span>
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setPagamento("pix")}
                data-testid={`booking-form-payment-pix-${cabin.id}`}
                className={`flex-1 min-w-[120px] py-2.5 rounded-lg text-sm font-bold border transition-colors ${
                  pagamento === "pix"
                    ? "bg-[#2d5a27] text-white border-[#2d5a27]"
                    : "bg-white text-[#526559] border-[#e2d9cd] hover:border-[#2d5a27]"
                }`}
              >
                Pix
              </button>
              <button
                type="button"
                onClick={() => setPagamento("credito")}
                data-testid={`booking-form-payment-credito-${cabin.id}`}
                className={`flex-1 min-w-[120px] py-2.5 rounded-lg text-sm font-bold border transition-colors ${
                  pagamento === "credito"
                    ? "bg-[#2d5a27] text-white border-[#2d5a27]"
                    : "bg-white text-[#526559] border-[#e2d9cd] hover:border-[#2d5a27]"
                }`}
              >
                Cartão de Crédito
              </button>
              {pagamento === "credito" && (
                <select
                  value={parcelas}
                  onChange={(e) => setParcelas(e.target.value)}
                  data-testid={`booking-form-installments-select-${cabin.id}`}
                  className="flex-1 min-w-[160px] border border-[#e2d9cd] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/40 bg-white"
                >
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>
                      {n}x sem juros
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-bold text-[#1e2923]">Alguma solicitação especial?</span>
            <textarea
              rows={3}
              value={form.solicitacao}
              onChange={set("solicitacao")}
              data-testid={`booking-form-special-request-${cabin.id}`}
              className="border border-[#e2d9cd] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/40 bg-white resize-none"
            />
          </label>

          <div className="bg-[#f5f0eb] rounded-xl p-5 border border-[#e2d9cd]" data-testid={`booking-summary-${cabin.id}`}>
            <p className="text-xs uppercase tracking-wider text-[#526559] font-bold mb-3">Resumo dos valores</p>
            <div className="flex flex-col gap-1.5 text-sm text-[#1e2923]">
              <div className="flex justify-between">
                <span>
                  Diárias ({nights} x {formatBRL(cabin.preco)})
                </span>
                <span className="font-bold">{formatBRL(nights * cabin.preco)}</span>
              </div>
              <div className="flex justify-between">
                <span>Taxa pet</span>
                <span className="font-bold">{petFee ? formatBRL(petFee) : "—"}</span>
              </div>
              <div className="flex justify-between">
                <span>Decoração ({decorOpt.nome})</span>
                <span className="font-bold">{decorOpt.preco ? formatBRL(decorOpt.preco) : "—"}</span>
              </div>
              <div className="flex justify-between border-t border-[#e2d9cd] pt-2 mt-1 text-base">
                <span className="font-bold text-[#1b4332]">Total</span>
                <span className="font-bold text-[#b85d39]" data-testid={`booking-total-price-display-${cabin.id}`}>
                  {formatBRL(total)}
                </span>
              </div>
            </div>
          </div>

          {error && (
            <p className="text-sm font-bold text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5" data-testid={`booking-error-${cabin.id}`}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={sending}
            data-testid={`booking-submit-whatsapp-button-${cabin.id}`}
            className="w-full bg-[#b85d39] hover:bg-[#9d4e2f] disabled:opacity-60 text-white font-bold py-4 rounded-full text-base transition-all duration-300 hover:-translate-y-0.5 shadow-lg flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Enviando...
              </>
            ) : (
              "Solicitar Reserva via WhatsApp"
            )}
          </button>
        </form>
      </div>

      <Dialog open={success} onOpenChange={setSuccess}>
        <DialogContent className="max-w-md" data-testid={`success-modal-container-${cabin.id}`}>
          <div className="flex flex-col items-center text-center gap-4 py-4">
            <div className="h-16 w-16 rounded-full bg-[#2d5a27] flex items-center justify-center">
              <Check className="h-9 w-9 text-white" />
            </div>
            <DialogTitle asChild>
              <h3 className="text-2xl font-bold text-[#1b4332]" data-testid={`success-modal-message-text-${cabin.id}`}>
                Sua reserva foi concluída com Sucesso!
              </h3>
            </DialogTitle>
            <p className="text-sm text-[#526559] leading-relaxed">
              Recebemos os dados da sua reserva da <strong>{cabin.nome}</strong> (
              {range?.from && format(range.from, "dd/MM/yyyy")} a{" "}
              {range?.to && format(range.to, "dd/MM/yyyy")}) no valor total de{" "}
              <strong>{formatBRL(total)}</strong>. Em breve entraremos em contato pelo seu
              WhatsApp para confirmar.
            </p>
            <a
              href={`https://wa.me/${CONTATO.whatsappLink}?text=${encodeURIComponent(
                `Olá! Acabei de solicitar uma reserva da ${cabin.nome} no site da Vila Gramado.`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`success-modal-whatsapp-link-${cabin.id}`}
              className="bg-[#2d5a27] hover:bg-[#1b4332] text-white font-bold px-6 py-3 rounded-full text-sm transition-colors"
            >
              Falar no WhatsApp
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </article>
  );
};
